'use client';

import { useMemo } from 'react';

import { FundingType, IFunding } from '@bytel/leviator';
import { Justify, Text, TextLevels, View } from '@bytel/trilogy-react';

import { DiscountEnumType } from '@app-types/discount';
import { PriceFundingsDetails } from '@app-types/product';

import { FundingDetailsBox } from '@components/funding-details-box';
import { InlinePrice } from '@components/inline-price';
import { PriceDetailToday, PriceTotalToday } from '@components/product-details-modal';

import { getDiscountValue } from '@helpers/product';

type FundingProps = {
    productDetails: PriceFundingsDetails;
    funding?: IFunding;
};

export function Funding({ productDetails: { discounts, price }, funding }: Readonly<FundingProps>) {
    const sumODRDiscount = getDiscountValue(discounts, DiscountEnumType.ODR);

    const isYounitedOrSwappyFunding = useMemo(() => {
        return !!funding && (funding.type === FundingType.YOUNITED || funding.type === FundingType.SWAPPY);
    }, [funding?.type]);

    if (!funding && sumODRDiscount > 0) {
        return (
            <>
                <PriceDetailToday value={sumODRDiscount} />
                <PriceTotalToday value={price.final - sumODRDiscount} />
            </>
        );
    } else if (!funding) {
        return null;
    }

    return (
        <>
            {funding.type !== FundingType.COMPTANT && (
                <View flexable justify={Justify.SPACE_BETWEEN}>
                    <Text level={TextLevels.TWO}>À régler aujourd&apos;hui</Text>
                    <InlinePrice typo={[]} level={TextLevels.TWO} amount={funding.initialDeposit} />
                </View>
            )}
            {!isYounitedOrSwappyFunding && sumODRDiscount > 0 && <PriceDetailToday value={sumODRDiscount} />}
            {Boolean(funding.installments) && (
                <View flexable justify={Justify.SPACE_BETWEEN}>
                    <div>
                        <Text level={TextLevels.TWO} marginless>
                            Puis à payer par mois
                        </Text>
                        <Text level={TextLevels.THREE} marginless>
                            {isYounitedOrSwappyFunding ? 'Financement' : 'Facilité '} sur {funding.installments} mois
                            {isYounitedOrSwappyFunding ? '\u00A0*' : ''}
                        </Text>
                    </div>

                    <InlinePrice typo={[]} level={TextLevels.TWO} amount={funding.monthlyAmount} period='mois' />
                </View>
            )}
            {isYounitedOrSwappyFunding && sumODRDiscount > 0 && <PriceDetailToday value={sumODRDiscount} />}
            {sumODRDiscount > 0 && (
                <PriceTotalToday
                    value={
                        isYounitedOrSwappyFunding
                            ? price.final - sumODRDiscount + funding.interestAmount
                            : price.final - sumODRDiscount
                    }
                />
            )}
            {isYounitedOrSwappyFunding && <FundingDetailsBox funding={funding} shouldShowFinancingText />}
        </>
    );
}
